import React, { createContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import RouteConstants from "../../../constants/routeConstants.json";
import { addPlanApi, getPlanFeatures } from "../../../api/Api";
import ContentData from "../../../constants/contentFile.json";
import {
  validateName,
  validateAmount,
  spaceCheck,
  validateNameWithNum,
} from "../../../utils/service";
import { useCookies } from "react-cookie";

export const AddPlanContext = createContext({});
export const AddPlanContextProvider = ({ children }) => {
  const [spinnerStatus, setSpinnerStatus] = useState(false);
  const [planName, setPlanName] = useState("");
  const [validatedName, setValidatedName] = useState("");
  const [price, setPrice] = useState("");
  const [maximumUsers, setMaximumUsers] = useState("");
  const [billing, setBilling] = useState("");
  const [description, setDescription] = useState("");
  const [features, setFeatures] = useState([]);
  const [validationPricing, setValidationPricing] = useState("");
  const [cookies, setCookie, removeCookie] = useCookies(["user"]);
  const [validationMaximumUser, setValidationMaximumUser] = useState("");
  const [validationDescription, setValidationDescription] = useState("");
  const [validationBilling, setValidationBilling] = useState("");
  const [bill, setBill] = useState("");
  const [successAlertShow, setSuccessAlertShow] = useState(false);
  const [successAlertMessage, setSuccessAlertMessage] = useState("");
  const [errorAlertShow, setErrorAlertShow] = useState(false);
  const [errorAlertMessage, setErrorAlertMessage] = useState("");
  const [buttonText, setButtonText] = useState("");
  const [statusValue, setStatusValue] = useState(0);
  const [checkedFeatures, setCheckedFeatures] = useState([]);
  const [trialShow, setTrialShow] = useState(false);
  const [trialDuration, setTrialDuration] = useState("");

  const [errorPopup, setErrorPopup] = useState(false);

  const navigate = useNavigate();
  const descriptionValidation = (value) => {
    if (value === "") {
      setValidationDescription(ContentData.planManagement.enterDescriptionData);
      return false;
    } else {
      setValidationDescription("");
      return true;
    }
  };

  const handledescription = (e) => {
    const value = e.target.value;
    setDescription(value);

    // Clear error dynamically if the name is valid
    if (validationDescription && descriptionValidation(value)) {
      setValidationDescription("");
    }
  };

  const nameValidation = (value) => {
    if (value === "") {
      setValidatedName(ContentData.planManagement.planenterName);
      return false;
    } else if (!validateNameWithNum(value)) {
      setValidatedName(ContentData.planManagement.planvalidName);
      return false;
    } else {
      setValidatedName("");
      return true;
    }
  };

  const handleName = (e) => {
    const value = e.target.value;
    const capitalizedValue = value.charAt(0).toUpperCase() + value.slice(1);
    setPlanName(capitalizedValue);

    // Clear error dynamically if the name is valid
    if (validateName && nameValidation(capitalizedValue)) {
      setValidatedName("");
    }
  };

  const userValidation = (value) => {
    if (value === "") {
      setValidationMaximumUser(ContentData.planManagement.userCount);
      return false;
    } else if (validateName(value)) {
      setValidationMaximumUser(ContentData.planManagement.uservalidData);
      return false;
    } else {
      setValidationMaximumUser("");
      return true;
    }
  };

  const handleMaximumUser = (e) => {
    const value = e.target.value;
    setMaximumUsers(value);

    if (validationMaximumUser && userValidation(value)) {
      setValidationMaximumUser("");
    }
  };

  const priceValidation = (value) => {
    if (value === "") {
      setValidationPricing(ContentData.planManagement.enterPrice);
      return false;
    } else if (validateName(value)) {
      setValidationPricing(ContentData.planManagement.pricevalidData);
      return false;
    } else {
      setValidationPricing("");
      return true;
    }
  };

  const handlePrice = (e) => {
    const value = e.target.value;
    setPrice(value);

    if (validationPricing && priceValidation(value)) {
      setValidationPricing("");
    }
  };

  const billValidation = (value) => {
    if (value === "") {
      setValidationBilling("Select");
      return false;
    } else {
      setValidationBilling("");
      return true;
    }
  };

  const handleBillChange = (e) => {
    setBill(e.target.value);
    billValidation(e.target.value);
  };

  const handleTrial = (e) => {
    setTrialDuration(e.target.value);
  };

  const billingData = [
    {
      id: "monthly",
      duration: "Monthly",
    },
    {
      id: "yearly",
      duration: "Yearly",
    },
  ];

  const trialPeriods = [
    {
      id: "7",
      duration: "7 days trial period",
    },
    {
      id: "15",
      duration: "15 days trial period",
    },
    {
      id: "30",
      duration: "30 days trial period",
    },
  ];

  const getFeatureData = async () => {
    try {
      const data = await getPlanFeatures();
      if (data?.data?.statusCode === 200) {
        console.log("data feat", data);
        setFeatures(data?.data?.data);
      }
    } catch (error) {
      const responseStatus = error?.response?.data?.statusCode;
      const errorMessage =
        error?.response?.data?.message || "Something went wrong...";

      setStatusValue(responseStatus);
      setErrorAlertMessage(errorMessage);
      setErrorPopup(true);

      if (responseStatus === 400) {
        setButtonText("Try Again");
      } else if (responseStatus === 401 || responseStatus === 403) {
        setButtonText("Login");
      } else {
        setButtonText("Try Again");
      }
    }
  };

  useEffect(() => {
    getFeatureData();
  }, []);

  const goToLogin = () => {
    removeCookie("x-access-token");
    navigate(RouteConstants.LOGIN);
  };
  // Convert checked features to the desired format
  const getFormattedFeatures = () => {
    return checkedFeatures.map((id) => ({ planFeature: id }));
  };

  const addPlanSubmit = async (e) => {
    e.preventDefault();
    const planDetails = {
      name: planName,
      billingCycle: bill,
      userCount: maximumUsers,
      price: price,
      description: description,
      features: checkedFeatures.map((id) => ({ planFeature: id })),
      trialPeriod: trialDuration ? trialDuration : 0,
    };
    const nameValidate = nameValidation(planName);
    const priceValidate = priceValidation(price);
    const userValidate = userValidation(maximumUsers);
    const billingValidate = billValidation(bill);
    const descriptionValidate = descriptionValidation(description);

    if (
      nameValidate &&
      priceValidate &&
      userValidate &&
      billingValidate &&
      descriptionValidate
    ) {
      try {
        setSpinnerStatus(true);
        const getData = await addPlanApi(planDetails);
        if (getData?.data?.statusCode === 200) {
          setErrorAlertShow(false);
          setSuccessAlertShow(true);
          setSpinnerStatus(false);
          setSuccessAlertMessage(getData?.data?.message);
        }
      } catch (error) {
        const responseStatus = error?.response?.data?.statusCode;
        const errorMessage =
          error?.response?.data?.message || "Something went wrong...";

        setStatusValue(responseStatus);

        setErrorAlertMessage(errorMessage);
        setErrorAlertShow(true);
        setSuccessAlertShow(false);

        if (responseStatus === 400) {
          setButtonText("Try Again");
          setSpinnerStatus(false);
        } else if (responseStatus === 401 || responseStatus === 403) {
          setButtonText("Login");
        } else {
          setButtonText("Try Again");
        }
      }
      setSpinnerStatus(false);
    }
  };

  // Handle checkbox toggle
  const handleCheckboxChange = (id) => {
    setCheckedFeatures((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const planListView = () => {
    navigate(RouteConstants.LIST_PLAN);
  };

  const providerValue = {
    ContentData,
    spinnerStatus,
    planName,
    price,
    maximumUsers,
    billing,
    description,
    features,
    handleName,
    nameValidation,
    validatedName,
    handlePrice,
    billingData,
    priceValidation,
    validationPricing,
    handleMaximumUser,
    validationMaximumUser,
    userValidation,
    handleBillChange,
    validationBilling,
    descriptionValidation,
    handledescription,
    validationDescription,
    addPlanSubmit,
    checkedFeatures,
    setCheckedFeatures,
    handleCheckboxChange,
    trialShow,
    setTrialShow,
    trialPeriods,
    handleTrial,
    trialPeriods,
    successAlertShow,
    successAlertMessage,
    errorAlertShow,
    setErrorAlertShow,
    errorAlertMessage,
    buttonText,
    statusValue,
    planListView,
    goToLogin,
    errorPopup,
    setErrorPopup,
    getFeatureData,
  };
  return (
    <AddPlanContext.Provider value={providerValue}>
      {children}
    </AddPlanContext.Provider>
  );
};
